/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useState } from 'react';
import { IntlProvider } from 'use-intl';
import translations from '../translations';

export type Language = 'en' | 'zh-CN' | 'vi' | 'ja' | 'es' | 'it' | 'ko' | 'de' | 'fr';

// Deep merge: locale-specific values override English defaults at every nesting level
function deepMerge(base: Record<string, any>, override: Record<string, any>): Record<string, any> {
  const result = { ...base };
  for (const key of Object.keys(override)) {
    if (
      result[key] &&
      typeof result[key] === 'object' &&
      !Array.isArray(result[key]) &&
      typeof override[key] === 'object' &&
      !Array.isArray(override[key])
    ) {
      result[key] = deepMerge(result[key], override[key]);
    } else {
      result[key] = override[key];
    }
  }
  return result;
}

type LanguageProviderProps = {
  children: React.ReactNode;
  defaultLanguage?: Language;
  storageKey?: string;
};

type LanguageProviderState = {
  language: Language;
  setLanguage: (lang: Language) => void;
};

const initialState: LanguageProviderState = {
  language: 'en',
  setLanguage: () => null,
};

const LanguageProviderContext = createContext<LanguageProviderState>(initialState);

export function LanguageProvider({
  children,
  defaultLanguage = 'en',
  storageKey = 'vite-ui-language',
  ...props
}: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>(
    () => (localStorage.getItem(storageKey) as Language) || defaultLanguage
  );

  const value = {
    language,
    setLanguage: (lang: Language) => {
      localStorage.setItem(storageKey, lang);
      setLanguage(lang);
    },
  };

  const localeMessages = translations[language === 'zh-CN' ? 'zhCN' : (language as keyof typeof translations)];
  const messages = useMemo(
    () => (language === 'en' ? translations.en : deepMerge(translations.en, localeMessages)),
    [language, localeMessages]
  );

  return (
    <LanguageProviderContext.Provider {...props} value={value}>
      <IntlProvider
        locale={language === 'zh-CN' ? 'zh-CN' : language}
        messages={messages}
        onError={() => {}}
      >
        {children}
      </IntlProvider>
    </LanguageProviderContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageProviderContext);
  if (context === undefined) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};
